import React, { useState } from 'react';
import LivenessService from '../services/LivenessService';
import EnhancedAttendanceService from '../services/EnhancedAttendanceService';

const LivenessTestScreen: React.FC = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [punchCode, setPunchCode] = useState('');

  const testCreateSession = async () => {
    setIsLoading(true);
    setResult('Creating liveness session...');
    
    try {
      const session = await LivenessService.createSession();
      setSessionId(session.sessionId);
      setResult(`✅ Session created successfully!\nSession ID: ${session.sessionId}`);
    } catch (error) {
      setResult(`❌ Error creating session: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testVerifyLiveness = async () => {
    if (!sessionId) {
      setResult('❌ Please create a session first');
      return;
    }

    setIsLoading(true);
    setResult('Verifying liveness...');
    
    try {
      const verificationResult = await LivenessService.verifyLiveness(sessionId);
      setResult(`${verificationResult.success ? '✅' : '❌'} Liveness verification result:
Success: ${verificationResult.success}
Message: ${verificationResult.message}
${verificationResult.confidence ? `Confidence: ${(verificationResult.confidence * 100).toFixed(1)}%` : ''}`);
    } catch (error) {
      setResult(`❌ Error verifying liveness: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testCompleteLivenessFlow = async () => {
    setIsLoading(true);
    setResult('Starting complete liveness flow...');
    
    try {
      const verificationResult = await LivenessService.completeLivenessFlow();
      setResult(`${verificationResult.success ? '✅' : '❌'} Complete liveness flow result:
Success: ${verificationResult.success}
Session ID: ${verificationResult.sessionId}
Message: ${verificationResult.message}
${verificationResult.confidence ? `Confidence: ${(verificationResult.confidence * 100).toFixed(1)}%` : ''}`);
      
      if (verificationResult.success) {
        setSessionId(verificationResult.sessionId);
      }
    } catch (error) {
      setResult(`❌ Error in complete liveness flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testAttendanceWithLiveness = async () => {
    if (!punchCode.trim()) {
      setResult('❌ Please enter a punch code');
      return;
    }

    setIsLoading(true);
    setResult('Testing attendance marking with liveness...');
    
    try {
      // Mock location for testing
      const mockLatitude = 40.7128;
      const mockLongitude = -74.0060;
      
      const attendanceResult = await EnhancedAttendanceService.markEntry(
        punchCode,
        mockLatitude,
        mockLongitude
      );
      
      let resultText = `${attendanceResult.success ? '✅' : '❌'} Attendance marking result:
Success: ${attendanceResult.success}
Message: ${attendanceResult.message}`;

      if (attendanceResult.livenessResult) {
        resultText += `

Liveness Details:
- Verification: ${attendanceResult.livenessResult.success ? 'Passed' : 'Failed'}
- Session ID: ${attendanceResult.livenessResult.sessionId}
- Message: ${attendanceResult.livenessResult.message}`;
        
        if (attendanceResult.livenessResult.confidence) {
          resultText += `
- Confidence: ${(attendanceResult.livenessResult.confidence * 100).toFixed(1)}%`;
        }
      }

      if (attendanceResult.data) {
        resultText += `

Backend Response: ${JSON.stringify(attendanceResult.data, null, 2)}`;
      }

      setResult(resultText);
    } catch (error) {
      setResult(`❌ Error in attendance with liveness: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>AWS Liveness Service Test</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Punch Code for Testing:</h3>
        <input
          type="text"
          value={punchCode}
          onChange={(e) => setPunchCode(e.target.value)}
          placeholder="Enter punch code"
          style={{ 
            padding: '10px', 
            width: '200px', 
            marginRight: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Current Session ID:</h3>
        <input
          type="text"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          placeholder="Session ID will appear here"
          style={{ 
            padding: '10px', 
            width: '400px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={testCreateSession}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            margin: '5px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          1. Create Session
        </button>

        <button
          onClick={testVerifyLiveness}
          disabled={isLoading || !sessionId}
          style={{
            padding: '10px 20px',
            margin: '5px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (isLoading || !sessionId) ? 'not-allowed' : 'pointer'
          }}
        >
          2. Verify Liveness
        </button>

        <button
          onClick={testCompleteLivenessFlow}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            margin: '5px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          3. Complete Flow
        </button>

        <button
          onClick={testAttendanceWithLiveness}
          disabled={isLoading || !punchCode.trim()}
          style={{
            padding: '10px 20px',
            margin: '5px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (isLoading || !punchCode.trim()) ? 'not-allowed' : 'pointer'
          }}
        >
          4. Test Attendance + Liveness
        </button>
      </div>

      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        minHeight: '200px'
      }}>
        <h3>Result:</h3>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
          {isLoading ? 'Loading...' : result || 'No results yet. Click a button to test.'}
        </pre>
      </div>

      <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
        <h4>API Flow:</h4>
        <ol>
          <li><strong>POST /liveness/session</strong> - Creates a new liveness session and returns session_id</li>
          <li><strong>POST /liveness/verify</strong> - Verifies liveness using the session_id</li>
          <li><strong>POST /attendance/mark</strong> - Marks attendance with punch_code, latitude, longitude, device_type (only after successful liveness verification)</li>
        </ol>
      </div>
    </div>
  );
};

export default LivenessTestScreen;